'use client';

import { type ReactNode } from 'react';
import Link from 'next/link';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

export interface FaqItem {
  title: string;
  text: ReactNode;
}

export type FaqItems = Array<FaqItem>;

const FAQ_KEYS = ['gettingStarted', 'goal', 'accuracy'] as const;

export function Faq() {
  const { t } = useTranslation();

  const items: FaqItems = FAQ_KEYS.map((key) => ({
    title: t(`faq.items.${key}.title`),
    text: key === 'accuracy' ? (
      <>
        {t('faq.items.accuracy.text')}{' '}
        <Link href="/datenqualitaet" className="underline hover:text-primary transition-colors">
          {t('faq.dataQualityLink')}
        </Link>
      </>
    ) : t(`faq.items.${key}.text`),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">{t('faq.title')}</CardTitle>
      </CardHeader>
      <CardContent className="py-3">
        <Accordion type="single" collapsible>
          {items.map((item, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger>{item.title}</AccordionTrigger>
              {/* forceMount keeps collapsed answers in the SSR HTML for
                  crawlers; Radix still sets `hidden` on closed items, so
                  the UI is unchanged (only the close animation is skipped). */}
              <AccordionContent forceMount>{item.text}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
}
